/**
 * Freshness classes — per-query `staleTime` values ratified by ADR-0008
 * (docs/adr/0008-query-client-defaults-and-data-freshness.md).
 *
 * The GLOBAL default stays `staleTime: 0` (queryClient.ts): every mount
 * shows cached data instantly and revalidates in the background, and
 * mutations invalidate via `useEntityInvalidate` / `useCrudInvalidate` /
 * the MutationCache prefix predicate. A query only declares a class when
 * its data provably tolerates staleness — never as an invalidation
 * workaround (CLAUDE.md "Never Use staleTime: 0 as a Workaround" applies
 * in both directions).
 *
 * Values are the ones already in production use (grep-verified
 * 2026-06-11: 15 occurrences / 6 files) — adopting a class here is a
 * refactor, not a behavior change.
 */
export const FRESHNESS = {
  /**
   * Server state that must be revalidated on every mount — the global
   * default. Exported so new code can declare the choice explicitly
   * instead of omitting the option.
   */
  REALTIME: 0,
  /**
   * Operational lists under active mutation by other users (gate-pass
   * lists, TTN board, accounting transactions/invoices). 30 s absorbs
   * tab-switch refetch storms while staying near-live.
   */
  LIST: 30_000,
  /**
   * Point lookups and dashboard aggregates (gate-pass by number, payment
   * orders by transportation, accounting dashboard, update checker).
   */
  LOOKUP: 60_000,
  /**
   * Slow-moving reference/feed data (notification list seed). Minutes of
   * staleness are invisible to the user.
   */
  REFERENCE: 5 * 60_000,
  /**
   * External third-party data with its own refresh cadence (weather).
   */
  EXTERNAL: 30 * 60_000,
} as const

export type FreshnessClass = keyof typeof FRESHNESS
